import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';
import { RefreshCw, Plus, Edit2, Trash2, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { PaginationWrapper } from '../ui/PaginationWrapper';

import { useAppDispatch, useAppSelector } from '../../store/store';
import { fetchAdminCoupons, fetchAdminPlans, fetchAdminPremiumPlans } from '../../store/slices/adminSlice';

const API_URL = BACKEND_CONFIG.apiUrl;

interface EnhancedCouponsViewProps {
  currentTheme: any;
}

export function EnhancedCouponsView({ currentTheme }: EnhancedCouponsViewProps) {
  const dispatch = useAppDispatch();
  const { coupons: couponsState, plans: plansState, premiumPlans: premiumPlansState } = useAppSelector(state => state.admin);
  const { list: coupons, loading: couponsLoading, pagination } = couponsState;
  const { list: plans, loading: plansLoading } = plansState;
  const { list: premiumPlans, loading: premiumPlansLoading } = premiumPlansState;
  const { totalPages } = pagination;

  const [isOpen, setIsOpen] = useState(false);
  const [isPremiumOpen, setIsPremiumOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const [formData, setFormData] = useState({
    code: '',
    discount: 10,
    selectedPlan: '',
    expiryDays: 30,
    maxUsageLimit: 0,
    description: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const loading = couponsLoading || plansLoading || premiumPlansLoading;

  useEffect(() => {
    dispatch(fetchAdminCoupons({ page: currentPage, limit: itemsPerPage }));
    dispatch(fetchAdminPlans());
    dispatch(fetchAdminPremiumPlans());
  }, [dispatch, currentPage, itemsPerPage]);

  const fetchData = async () => {
    dispatch(fetchAdminCoupons({ page: currentPage, limit: itemsPerPage }));
    dispatch(fetchAdminPlans());
    dispatch(fetchAdminPremiumPlans());
  };

  const handleOpenEdit = (coupon: any) => {
    setEditingCoupon(coupon);

    const now = new Date();
    const expiry = new Date(coupon.validUntil);
    const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    setFormData({
      code: coupon.code,
      discount: coupon.discountPercentage || 10,
      selectedPlan: typeof coupon.planId === 'object' ? coupon.planId._id : coupon.planId,
      expiryDays: daysUntilExpiry > 0 ? daysUntilExpiry : 30,
      maxUsageLimit: coupon.maxUses || 0,
      description: coupon.description || ''
    });
    setIsOpen(true);
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    setIsPremiumOpen(false);
    setEditingCoupon(null);
    setFormData({
      code: '',
      discount: 10,
      selectedPlan: '',
      expiryDays: 30,
      maxUsageLimit: 0,
      description: ''
    });
  };

  const handleSaveCoupon = async (isPremium = false) => {
    if (!formData.code || !formData.selectedPlan) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!editingCoupon && formData.code.length < 4) {
      toast.error('Coupon code must be at least 4 characters');
      return;
    }

    // Debug: Log what type of coupon we're creating
    console.log('🔍 ===== FRONTEND CREATE COUPON DEBUG =====');
    console.log('🔍 handleSaveCoupon called with isPremium:', isPremium);
    console.log('🔍 isPremium type:', typeof isPremium);
    console.log('🔍 isPremium === true:', isPremium === true);
    console.log('🔍 Selected plan ID:', formData.selectedPlan);
    try {
      console.log('🔍 Available subscription plans:', plans?.length, plans?.map(p => ({ id: p?._id, name: p?.name })));
      const safePremiumPlans = Array.isArray(premiumPlans) ? premiumPlans : [];
      console.log('🔍 Available premium plans:', safePremiumPlans.length, safePremiumPlans.map(p => ({ id: p?._id, name: p?.name })));
    } catch (e) {
      console.warn('Error in debug logging:', e);
    }
    console.log('🔍 ==========================================');

    try {
      console.log('🔍 [FRONTEND] Starting save process...');
      setCreating(true);
      const token = localStorage.getItem('token');
      console.log('🔍 [FRONTEND] Token present:', !!token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + formData.expiryDays);
      console.log('🔍 [FRONTEND] validUntilDate:', validUntilDate.toISOString());

      const payload: any = {
        planId: formData.selectedPlan,
        planModel: isPremium ? 'PremiumPlan' : 'Plan', // Explicitly set model
        discountPercentage: formData.discount,
        maxUses: formData.maxUsageLimit,
        validUntil: validUntilDate.toISOString(),
        description: formData.description || ''
      };

      if (isPremium) {
        payload.planType = 'premium_plan';
      }

      console.log('🔍 [FRONTEND] Payload ready:', payload);

      if (editingCoupon) {
        console.log('🔍 [FRONTEND] Updating coupon:', editingCoupon._id);
        const response = await axios.put(`${API_URL}/coupons/${editingCoupon._id}`, payload);
        console.log('🔍 [FRONTEND] Update response:', response.data);
        if (response.data.success) {
          toast.success('Coupon updated successfully!');
          handleCloseDialog();
          fetchData();
        } else {
          console.error('🔍 [FRONTEND] Update failed (success=false):', response.data);
          toast.error(response.data.message || 'Failed to update coupon');
        }
      } else {
        console.log('🔍 [FRONTEND] Creating new coupon...');
        const response = await axios.post(`${API_URL}/coupons`, {
          code: formData.code.toUpperCase(),
          ...payload
        });
        console.log('🔍 [FRONTEND] Create response:', response.data);
        if (response.data.success) {
          toast.success('Coupon created successfully!');
          handleCloseDialog();
          fetchData();
        } else {
          console.error('🔍 [FRONTEND] Create failed (success=false):', response.data);
          toast.error(response.data.message || 'Failed to create coupon');
        }
      }
    } catch (error: any) {
      console.error('🔍 [FRONTEND] CRITICAL ERROR in handleSaveCoupon:', error);
      console.error('🔍 [FRONTEND] Error response:', error.response?.data);
      toast.error(error.response?.data?.message || error.message || 'Failed to save coupon');
    } finally {
      console.log('🔍 [FRONTEND] Resetting creating state.');
      setCreating(false);
    }
  };

  const handleDeleteCoupon = async (couponId: string) => {
    if (!confirm('Are you sure you want to delete this coupon?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.delete(`${API_URL}/coupons/${couponId}`);
      if (response.data.success) {
        toast.success('Coupon deleted successfully');
        fetchData();
      }
    } catch (error: any) {
      console.error('Error deleting coupon:', error);
      toast.error('Failed to delete coupon');
    }
  };

  const handleToggleCoupon = async (couponId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('token');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await axios.put(`${API_URL}/coupons/${couponId}/toggle`);
      if (response.data.success) {
        toast.success(`Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling coupon:', error);
      toast.error('Failed to update coupon status');
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null || isNaN(amount)) {
      return '₹0';
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPlanName = (coupon: any) => {
    if (typeof coupon.planId === 'object' && coupon.planId?.name) {
      return coupon.planId.name;
    }
    return 'Unknown Plan';
  };

  const getCreatorName = (coupon: any) => {
    if (coupon.createdBy) {
      return `${coupon.createdBy.firstName} ${coupon.createdBy.lastName}`;
    }
    return 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="size-8 animate-spin text-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold">Coupon Management</h3>
            <p className="text-sm text-gray-500 mt-1">{coupons.length} coupons found</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="size-4 mr-2" />
              Refresh
            </Button>

            {/* Subscription Coupon Dialog */}
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (open && !editingCoupon) {
                // Clear form data when opening subscription dialog
                setFormData({
                  code: '',
                  discount: 10,
                  selectedPlan: '',
                  expiryDays: 30,
                  maxUsageLimit: 0,
                  description: ''
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-rose-500 to-pink-500 hover:opacity-90">
                  <Plus className="size-4 mr-2" />
                  Subscription Coupon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Subscription Coupon</DialogTitle>
                </DialogHeader>

                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border-2 border-pink-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <Label>Coupon Code *</Label>
                      <Input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., SAVE20"
                        className="font-mono"
                        disabled={!!editingCoupon}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {editingCoupon ? 'Code cannot be changed' : 'Min 4 characters'}
                      </p>
                    </div>

                    <div>
                      <Label>Discount Percentage *</Label>
                      <Select
                        value={formData.discount.toString()}
                        onValueChange={(value) => setFormData({ ...formData, discount: Number(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="10">10%</SelectItem>
                          <SelectItem value="15">15%</SelectItem>
                          <SelectItem value="20">20%</SelectItem>
                          <SelectItem value="25">25%</SelectItem>
                          <SelectItem value="30">30%</SelectItem>
                          <SelectItem value="40">40%</SelectItem>
                          <SelectItem value="50">50%</SelectItem>
                          <SelectItem value="60">60%</SelectItem>
                          <SelectItem value="70">70%</SelectItem>
                          <SelectItem value="80">80%</SelectItem>
                          <SelectItem value="90">90%</SelectItem>
                          <SelectItem value="100">100% (Free Plan)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Applicable Plan *</Label>
                      <Select
                        value={formData.selectedPlan}
                        onValueChange={(value) => setFormData({ ...formData, selectedPlan: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {plans.map(plan => (
                            <SelectItem key={plan._id} value={plan._id}>
                              {plan.name} - {formatCurrency(plan.finalPrice || plan.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Valid For (Days)</Label>
                      <Input
                        type="number"
                        value={formData.expiryDays}
                        onChange={(e) => setFormData({ ...formData, expiryDays: Number(e.target.value) })}
                        min="1"
                        max="365"
                      />
                      <p className="text-xs text-gray-500 mt-1">Days until expiration</p>
                    </div>

                    <div>
                      <Label>Usage Limit (0 for unlimited)</Label>
                      <Input
                        type="number"
                        value={formData.maxUsageLimit}
                        onChange={(e) => setFormData({ ...formData, maxUsageLimit: Number(e.target.value) })}
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max times this can be used</p>
                    </div>

                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="e.g., Limited time offer"
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">For internal reference</p>
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-3">
                    <Button
                      onClick={() => handleSaveCoupon(false)}
                      disabled={creating || !formData.code || !formData.selectedPlan}
                      className="flex-1 bg-pink-500 hover:bg-pink-600"
                    >
                      {creating ? (
                        <>
                          <RefreshCw className="size-4 mr-2 animate-spin" />
                          {editingCoupon ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingCoupon ? 'Update Coupon' : 'Create Coupon'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCloseDialog}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Addon Coupon Dialog */}
            <Dialog open={isPremiumOpen} onOpenChange={(open) => {
              setIsPremiumOpen(open);
              if (open && !editingCoupon) {
                // Clear form data when opening addon dialog
                setFormData({
                  code: '',
                  discount: 10,
                  selectedPlan: '',
                  expiryDays: 30,
                  maxUsageLimit: 0,
                  description: ''
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90">
                  <Plus className="size-4 mr-2" />
                  Addon Coupon
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Addon/Premium Plan Coupon</DialogTitle>
                </DialogHeader>

                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border-2 border-purple-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                    <div>
                      <Label>Coupon Code *</Label>
                      <Input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        placeholder="e.g., ADDON20"
                        className="font-mono"
                      />
                      <p className="text-xs text-gray-500 mt-1">Min 4 characters</p>
                    </div>

                    <div>
                      <Label>Discount Percentage *</Label>
                      <Select
                        value={formData.discount.toString()}
                        onValueChange={(value) => setFormData({ ...formData, discount: Number(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="10">10%</SelectItem>
                          <SelectItem value="15">15%</SelectItem>
                          <SelectItem value="20">20%</SelectItem>
                          <SelectItem value="25">25%</SelectItem>
                          <SelectItem value="30">30%</SelectItem>
                          <SelectItem value="40">40%</SelectItem>
                          <SelectItem value="50">50%</SelectItem>
                          <SelectItem value="60">60%</SelectItem>
                          <SelectItem value="70">70%</SelectItem>
                          <SelectItem value="80">80%</SelectItem>
                          <SelectItem value="90">90%</SelectItem>
                          <SelectItem value="100">100% (Free Plan)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Applicable Addon Plan *</Label>
                      <Select
                        value={formData.selectedPlan}
                        onValueChange={(value) => setFormData({ ...formData, selectedPlan: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select addon plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {premiumPlans.length > 0 ? (
                            premiumPlans.map(plan => (
                              <SelectItem key={plan._id} value={plan._id}>
                                {plan.name} - {formatCurrency(plan.finalPrice || plan.price)}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-plans" disabled>No addon plans available</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {premiumPlans.length === 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          No addon plans found. Please create addon plans first in Premium Plans section.
                        </p>
                      )}
                    </div>

                    <div>
                      <Label>Valid For (Days)</Label>
                      <Input
                        type="number"
                        value={formData.expiryDays}
                        onChange={(e) => setFormData({ ...formData, expiryDays: Number(e.target.value) })}
                        min="1"
                        max="365"
                      />
                      <p className="text-xs text-gray-500 mt-1">Days until expiration</p>
                    </div>

                    <div>
                      <Label>Usage Limit (0 for unlimited)</Label>
                      <Input
                        type="number"
                        value={formData.maxUsageLimit}
                        onChange={(e) => setFormData({ ...formData, maxUsageLimit: Number(e.target.value) })}
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max times this can be used</p>
                    </div>

                    <div>
                      <Label>Description (optional)</Label>
                      <Input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="e.g., Limited time offer"
                        maxLength={100}
                      />
                      <p className="text-xs text-gray-500 mt-1">For internal reference</p>
                    </div>
                  </div>

                  <div className="flex gap-2 sm:gap-3">
                    <Button
                      onClick={() => handleSaveCoupon(true)}
                      disabled={creating || !formData.code || !formData.selectedPlan || premiumPlans.length === 0}
                      className="flex-1 bg-purple-500 hover:bg-purple-600"
                    >
                      {creating ? (
                        <>
                          <RefreshCw className="size-4 mr-2 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        'Create Addon Coupon'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCloseDialog}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Enhanced Coupons Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold">Code</th>
                <th className="text-left p-4 font-semibold">Plan</th>
                <th className="text-left p-4 font-semibold">Discount</th>
                <th className="text-left p-4 font-semibold">Usage</th>
                <th className="text-left p-4 font-semibold">Created By</th>
                <th className="text-left p-4 font-semibold">Valid Until</th>
                <th className="text-left p-4 font-semibold">Status</th>
                <th className="text-left p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {coupons.map((coupon) => (
                <tr key={coupon._id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-mono font-bold bg-blue-50 text-blue-700 px-3 py-1 rounded w-fit">
                        {coupon.code}
                      </span>
                      {coupon.description && (
                        <span className="text-xs text-gray-500 italic">"{coupon.description}"</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm font-medium">{getPlanName(coupon)}</span>
                  </td>
                  <td className="p-4">
                    <span className="font-semibold text-green-600">{coupon.discountPercentage || 0}%</span>
                  </td>
                  <td className="p-4">
                    <div className="text-sm">
                      <div className="font-medium">{coupon.usedCount || 0} used</div>
                      <div className="text-xs text-gray-500">
                        {coupon.maxUses > 0 ? `Limit: ${coupon.maxUses}` : 'Unlimited'}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <User className="size-4 text-gray-400" />
                      <div className="text-sm">
                        <div className="font-medium">{getCreatorName(coupon)}</div>
                        {coupon.creatorType && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {coupon.creatorType}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-sm">
                    {coupon.validUntil ? formatDate(coupon.validUntil) : 'N/A'}
                  </td>
                  <td className="p-4">
                    <Badge className={coupon.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                      {coupon.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(coupon)}
                      >
                        <Edit2 className="size-4" />
                      </Button>
                      <Switch
                        checked={coupon.isActive}
                        onCheckedChange={() => handleToggleCoupon(coupon._id, coupon.isActive)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCoupon(coupon._id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <PaginationWrapper
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={coupons.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        showItemsPerPage={false}
      />
    </div>
  );
}